import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FirestoreContextService } from './firestore-context.service';
import { AnalyticsService } from './analytics.service';
import { EVENTS_PAGE_SIZE, EventDoc, EventsPage } from '../models/event.model';
import { eventStartDate } from '../events/event-utils';

/**
 * Venue events, read straight from the `events` Firestore collection.
 *
 * Mirrors the iOS `GetEvents` / `GetEvent` structured queries (`FirebaseAPIRequests.swift`), but
 * through the `@angular/fire` SDK rather than the REST endpoint iOS posts to — the same choice
 * `BannerService` and `CatalogService` already make. Two things fall out of that and both are
 * improvements: the SDK decodes `prices[].price` to a JS number whether Firestore stored an integer
 * or a double (over REST those arrive as `"10"` and `10.0` and have to be normalized by hand), and
 * paging can use a real cursor.
 *
 * Reads are unauthenticated — verified against both projects, and iOS sends no auth header either,
 * so the security rules already permit this. No index work is needed: all four query shapes below
 * were run against production before this was written.
 *
 * Errors are swallowed to a `null` return, as in `AttractionsService`, so one dead query cannot take
 * the screen down — and each one reports itself so a silently empty Events tab is distinguishable
 * from a venue that genuinely has no events.
 */
@Injectable({ providedIn: 'root' })
export class EventsService {

  constructor(
    private afs: AngularFirestore,
    private fsContext: FirestoreContextService,
    private analytics: AnalyticsService
  ) {}

  private reportError(errorType: 'list' | 'details', error: any): null {
    this.analytics.log('events_error', {
      error_type: errorType,
      // GA4 truncates parameter values at 100 characters; trim rather than let it happen silently.
      error_message: String(error?.code ?? error?.message ?? error ?? 'unknown').slice(0, 100)
    });
    return null;
  }

  /**
   * Midnight local time — the lower bound on `eventDate.startDate`, matching iOS's
   * `Calendar.current.startOfDay(for: .now)`.
   *
   * The boundary is a day rather than "now" on purpose: an event that started at 20:00 yesterday
   * has dropped off, but one that started at 09:00 this morning is still today's event and stays
   * listed all day.
   */
  private startOfToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  /**
   * One page of events, oldest-starting first.
   *
   * @param shopId    Venue to scope to, or null/undefined for every venue's events. NOTE the type:
   *                  Firestore stores `businessId` as a STRING ("316") while the API's
   *                  `clientData.id` is a NUMBER (316) — so this coerces rather than trusting the
   *                  caller. A number here would match nothing and return an empty list with no
   *                  error, which is invisible in QA.
   * @param isFeatured Filter on the featured flag, or null/undefined to include both.
   * @param cursor    The previous page's `cursor`. Omit for the first page.
   */
  async getEvents(options: {
    shopId?: string | number | null;
    isFeatured?: boolean | null;
    cursor?: unknown | null;
    limit?: number;
  }): Promise<EventsPage | null> {
    const { shopId, isFeatured, cursor } = options;
    const limit = options.limit ?? EVENTS_PAGE_SIZE;

    try {
      const snapshot = await this.fsContext.run(() => this.afs.collection<EventDoc>('events', ref => {
        let query = ref
          .where('active', '==', true)
          .where('eventDate.startDate', '>=', this.startOfToday());

        if (shopId !== null && shopId !== undefined && String(shopId).trim() !== '') {
          query = query.where('businessId', '==', String(shopId));
        }
        if (isFeatured !== null && isFeatured !== undefined) {
          query = query.where('isFeatured', '==', isFeatured);
        }

        // Firestore requires the first orderBy to be the range-filtered field.
        query = query.orderBy('eventDate.startDate', 'asc');

        // The JS SDK has no offset() — paging is by cursor, which is also cheaper: an offset makes
        // the server walk and bill every skipped document.
        if (cursor) {
          query = query.startAfter(cursor);
        }
        return query.limit(limit);
      }).get()).toPromise();

      if (!snapshot) return { events: [], cursor: null };

      const events = snapshot.docs.map(doc => doc.data() as EventDoc);

      // A short page means the stream is exhausted. Returning a cursor anyway would cost the caller
      // one guaranteed-empty round trip per section on every venue with few events — i.e. most.
      const exhausted = snapshot.docs.length < limit;

      return {
        events,
        cursor: exhausted ? null : snapshot.docs[snapshot.docs.length - 1]
      };
    } catch (error) {
      return this.reportError('list', error);
    }
  }

  /**
   * The venue's event for TODAY, or null — what the first touristic notification is about.
   *
   * "Today" is the device's local day, resolved at scan time: the diner is standing in the venue, so
   * their phone's day and the venue's day are the same one. The window is a half-open
   * [midnight, next midnight) on `eventDate.startDate`, which is the same field and the same day
   * boundary the Events list already uses — one range filter, so no new index.
   *
   * DELIBERATELY ONLY EVENTS THAT *START* TODAY. A multi-day event that began yesterday and runs
   * through this evening is not counted, because `getEvents` filters it out too: it is not in the
   * list the notification would send the diner to, and notifying about something the app cannot show
   * them is worse than staying quiet.
   *
   * With several today the earliest one wins, and `isFeatured` breaks a tie between two that start at
   * the same minute — the same order the list itself renders them in. The page is small on purpose:
   * a venue with more than 10 events starting on one day is not a case worth a cursor.
   */
  async getTodaysEvent(shopId: string | number | null | undefined): Promise<EventDoc | null> {
    if (shopId === null || shopId === undefined || String(shopId).trim() === '') return null;

    const start = this.startOfToday();
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);

    try {
      const snapshot = await this.fsContext.run(() =>
        this.afs.collection<EventDoc>('events', ref => ref
          .where('active', '==', true)
          .where('businessId', '==', String(shopId))
          .where('eventDate.startDate', '>=', start)
          .where('eventDate.startDate', '<', end)
          .orderBy('eventDate.startDate', 'asc')
          .limit(10)
        ).get()
      ).toPromise();

      if (!snapshot || snapshot.empty) return null;

      const events = snapshot.docs.map(doc => doc.data() as EventDoc);
      const earliest = eventStartDate(events[0])?.getTime();
      // Already ordered by start, so the tie-break only has to look at the ones sharing that instant.
      const tied = events.filter(e => eventStartDate(e)?.getTime() === earliest);
      return tied.find(e => e.isFeatured === true) ?? events[0];
    } catch (error) {
      return this.reportError('list', error);
    }
  }

  /**
   * A single event by its `id` FIELD.
   *
   * Deliberately not `doc(id).get()`: the document key and the `id` field agree on most documents
   * but the field is what iOS queries and what the banner / push payloads carry, so matching on it
   * is the compatible choice. Returns null when nothing matches — the details screen renders its
   * not-found state rather than an error for that case.
   */
  async getEvent(eventId: string): Promise<EventDoc | null> {
    if (!eventId) return null;

    try {
      const snapshot = await this.fsContext.run(() =>
        this.afs.collection<EventDoc>('events', ref =>
          ref.where('id', '==', eventId).limit(1)
        ).get()
      ).toPromise();

      if (!snapshot || snapshot.empty) return null;
      return snapshot.docs[0].data() as EventDoc;
    } catch (error) {
      return this.reportError('details', error);
    }
  }

  /**
   * The events named in another event's `relatedFeedItems`, fetched concurrently and in the order
   * they were listed (iOS `fetchRelatedItems` collects them out of order; keeping the authored order
   * is what the admin actually chose).
   *
   * Ids that resolve to nothing are dropped. `relatedFeedItems` is empty on every document in both
   * projects today, so in practice this returns `[]` and the carousel self-hides.
   */
  async getRelatedEvents(eventIds: string[]): Promise<EventDoc[]> {
    const ids = eventIds.filter(id => !!id);
    if (!ids.length) return [];

    const results = await Promise.all(ids.map(id => this.getEvent(id)));
    return results.filter((e): e is EventDoc => e !== null);
  }
}
